/**
 * CatalogTableSettings — "Tabloyu Özelleştir" diyaloğu.
 *
 * Hangi sütunun görüneceğini seçtirir. Sabit sütunlar (sıra no, ürün adı, işlemler)
 * kapatılamaz; onlar olmadan tablo kullanılamaz hale gelir.
 *
 * Seçim localStorage'da saklanır, sunucuya gitmez.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

import { DEFAULT_CATALOG_COLUMNS } from '../types/catalog.types';
import type { CatalogColumnSetting } from '../types/catalog.types';

export interface CatalogTableSettingsProps {
  open: boolean;
  onClose: () => void;
  columns: CatalogColumnSetting[];
  onSave: (columns: CatalogColumnSetting[]) => void;
}

const CatalogTableSettings: React.FC<CatalogTableSettingsProps> = ({
  open,
  onClose,
  columns,
  onSave,
}) => {
  const [draft, setDraft] = useState<CatalogColumnSetting[]>(columns);

  // Diyalog açıldığında mevcut ayarı taslağa kopyala — "İptal" gerçekten iptal etsin.
  useEffect(() => {
    if (open) setDraft(columns.map((c) => ({ ...c })));
  }, [open, columns]);

  const toggle = (id: string) => {
    setDraft((prev) =>
      prev.map((col) => (col.id === id && !col.fixed ? { ...col, visible: !col.visible } : col))
    );
  };

  const fixedColumns = draft.filter((c) => c.fixed);
  const optionalColumns = draft.filter((c) => !c.fixed);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ViewColumnIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          Tabloyu Özelleştir
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
          >
            <LockIcon sx={{ fontSize: 16 }} /> Sabit Sütunlar
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Bu sütunlar her zaman görünür.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            {fixedColumns.map((col) => (
              <FormControlLabel
                key={col.id}
                control={<Switch checked disabled size="small" />}
                label={<Typography variant="body2">{col.label}</Typography>}
              />
            ))}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Görünürlüğü Değiştirilebilir Sütunlar
          </Typography>
          <Grid container spacing={1}>
            {optionalColumns.map((col) => (
              <Grid item xs={12} sm={6} key={col.id}>
                <FormControlLabel
                  control={
                    <Switch checked={col.visible} onChange={() => toggle(col.id)} size="small" />
                  }
                  label={<Typography variant="body2">{col.label}</Typography>}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button
          color="inherit"
          onClick={() => setDraft(DEFAULT_CATALOG_COLUMNS.map((c) => ({ ...c })))}
        >
          Varsayılana Dön
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button color="inherit" onClick={onClose}>
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onSave(draft);
            onClose();
          }}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogTableSettings;
